import mqtt from "mqtt";
import z from "zod";
import { ApiAuth } from "./auth";

const MQTT_URL = process.env.REACT_APP_MQTT_URL || "";
const MQTT_TOPIC = process.env.REACT_APP_MQTT_TOPIC || "";

export enum MessageState {
  Connecting,
  Connected,
  Disconnected,
}

export const messageOrderUpdatedSchema = z.object({
  type: z.literal("order"),
  id: z.string(),
});
export const messageOrgUpdatedSchema = z.object({
  type: z.literal("org"),
});
export const messageSchema = z.discriminatedUnion("type", [
  messageOrderUpdatedSchema,
  messageOrgUpdatedSchema,
]);

export type MessageOrderUpdated = z.infer<typeof messageOrderUpdatedSchema>;
export type MessageOrgUpdated = z.infer<typeof messageOrgUpdatedSchema>;
export type Message = z.infer<typeof messageSchema>;

export type OnMessageStateChange = (state: MessageState) => void;
export type OnOrderUpdated = (msg: MessageOrderUpdated) => void;
export type OnOrgUpdated = (msg: MessageOrgUpdated) => void;

export interface MessagesClient {
  disconnect: () => void;
  onMessageStateChange: (cb: OnMessageStateChange | undefined) => void;
  onOrderUpdated: (cb: OnOrderUpdated | undefined) => void;
  onOrgUpdated: (cb: OnOrgUpdated | undefined) => void;
}

export function createMessagesClient(auth: ApiAuth): MessagesClient {
  let state = MessageState.Connecting;
  let cbOnMessageStateChange: OnMessageStateChange | undefined = undefined;
  let cbOnOrderUpdated: OnOrderUpdated | undefined = undefined;
  let cbOnOrgUpdated: OnOrgUpdated | undefined = undefined;

  const client = mqtt.connect(MQTT_URL, {
    transformWsUrl: (url) => {
      console.log(
        "Transforming MQTT URL",
        url + "?token=" + auth.getSyncToken()
      );
      return url + "?token=" + auth.getSyncToken();
    },
  });
  client.on("reconnect", () => {
    state = MessageState.Connecting;
    if (cbOnMessageStateChange) {
      cbOnMessageStateChange(state);
    }
  });
  client.on("disconnect", () => {
    state = MessageState.Disconnected;
    if (cbOnMessageStateChange) {
      cbOnMessageStateChange(state);
    }
  });
  client.on("connect", () => {
    state = MessageState.Connected;
    if (cbOnMessageStateChange) {
      cbOnMessageStateChange(state);
    }
    client.subscribe(MQTT_TOPIC + "/" + auth.user.org, (err) => {
      if (err) {
        console.error(
          "MQTT subscribe error",
          MQTT_TOPIC + "/" + auth.user.org,
          err
        );
      }
    });
  });
  client.on("message", (topic, message) => {
    try {
      const msg = messageSchema.parse(JSON.parse(message.toString()));
      if (msg.type === "order") {
        if (cbOnOrderUpdated) {
          cbOnOrderUpdated(msg);
        }
      } else if (msg.type === "org") {
        if (cbOnOrgUpdated) {
          cbOnOrgUpdated(msg);
        }
      }
    } catch (error) {
      console.error("Message parse error", topic, message.toString(), error);
    }
  });
  client.on("error", (error) => {
    console.error("MQTT error", error);
  });
  client.on("offline", () => {
    state = MessageState.Disconnected;
    if (cbOnMessageStateChange) {
      cbOnMessageStateChange(state);
    }
  });
  function disconnect() {
    client.end();
  }

  function onMessageStateChange(cb: OnMessageStateChange | undefined) {
    cbOnMessageStateChange = cb;
    if (cbOnMessageStateChange) {
      cbOnMessageStateChange(state);
    }
  }

  function onOrderUpdated(cb: OnOrderUpdated | undefined) {
    cbOnOrderUpdated = cb;
  }

  function onOrgUpdated(cb: OnOrgUpdated | undefined) {
    cbOnOrgUpdated = cb;
  }

  return { disconnect, onMessageStateChange, onOrderUpdated, onOrgUpdated };
}
